import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FolderKanban, Users, Globe2, Sparkles, ArrowRight, RefreshCw } from "lucide-react";
import { getDashboardStats } from "../../services/crmService";
import { getSession } from "../../services/authService";
import BillingUsageCard from "../../components/billing/BillingUsageCard";

function getErrorMessage(error, fallback = "Failed to load dashboard.") {
    const apiError = error?.response?.data;

    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (apiError?.title) return apiError.title;
    if (error?.message) return error.message;

    return fallback;
}

function DashboardMetric({ icon: Icon, label, value, hint }) {
    return (
        <div className="dash-metric-card">
            <div className="dash-metric-icon">
                <Icon size={20} />
            </div>
            <span>{label}</span>
            <strong>{value || 0}</strong>
            <small>{hint}</small>
        </div>
    );
}

function QuickActionCard({ title, description, to, disabled }) {
    if (disabled) {
        return (
            <div className="dash-action-card disabled">
                <div>
                    <strong>{title}</strong>
                    <p>{description}</p>
                </div>
                <span>Coming soon</span>
            </div>
        );
    }

    return (
        <Link className="dash-action-card" to={to}>
            <div>
                <strong>{title}</strong>
                <p>{description}</p>
            </div>
            <ArrowRight size={18} />
        </Link>
    );
}

export default function ClientDashboard() {
    const user = getSession();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        try {
            setLoading(true);
            setError("");

            const res = await getDashboardStats();
            setStats(res || {});
        } catch (err) {
            console.error(err);
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const hasProjects = Number(stats?.totalProjects || 0) > 0;

    const summaryText = useMemo(() => {
        if (loading) return "Loading your workspace...";
        if (!hasProjects) return "No projects are assigned to this account yet.";
        if (Number(stats?.publishedProjects || 0) > 0) return "Your live website workspace is ready.";
        return "Your generated projects are ready for review and publishing.";
    }, [loading, hasProjects, stats?.publishedProjects]);

    return (
        <div className="dash-shell">
            <section className="dash-hero-card">
                <div>
                    <span className="project-kicker">Client Workspace</span>
                    <h2>Welcome{user?.name ? `, ${user.name}` : ""}</h2>
                    <p>{summaryText}</p>
                </div>

                <button
                    type="button"
                    className="project-action-btn secondary"
                    onClick={load}
                    disabled={loading}
                >
                    <RefreshCw size={16} />
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </section>

            {error && (
                <section className="dash-alert error">
                    <strong>Dashboard could not load</strong>
                    <p>{error}</p>
                </section>
            )}

            <section className="dash-metric-grid">
                <DashboardMetric
                    icon={FolderKanban}
                    label="My Projects"
                    value={stats?.totalProjects}
                    hint="Assigned websites"
                />
                <DashboardMetric
                    icon={Users}
                    label="Leads"
                    value={stats?.totalLeads}
                    hint="Website enquiries"
                />
                <DashboardMetric
                    icon={Globe2}
                    label="Published"
                    value={stats?.publishedProjects}
                    hint="Live websites"
                />
                <DashboardMetric
                    icon={Sparkles}
                    label="AI Optimized"
                    value={stats?.projectsWithVariants}
                    hint="Variant-enabled sites"
                />
            </section>

            <BillingUsageCard />

            {!loading && !hasProjects ? (
                <section className="dash-empty-workspace">
                    <div className="dash-empty-icon">🌐</div>
                    <h3>No projects assigned yet</h3>
                    <p>
                        Once your website project is assigned, you will be able to edit content,
                        publish changes, manage leads, and track deployment status from here.
                    </p>
                </section>
            ) : (
                <section className="dash-client-grid">
                    <div className="dash-panel">
                        <div className="dash-panel-head">
                            <div>
                                <span className="project-kicker">Quick Actions</span>
                                <h3>What would you like to do?</h3>
                                <p>Start with projects or leads. Website repair tools are available inside each project.</p>
                            </div>
                        </div>

                        <div className="dash-action-grid">
                            <QuickActionCard
                                title="Open Projects"
                                description="Edit CRM modules, publish changes, and review deployment workflow."
                                to="/projects"
                            />

                            <QuickActionCard
                                title="Review Leads"
                                description="Track website enquiries, assign owners, and update lead status."
                                to="/leads"
                            />

                            <QuickActionCard
                                title="Business Settings"
                                description="Manage contact details, buttons, CRM behavior, and usage settings."
                                to="/settings"
                            />

                            <QuickActionCard
                                title="AI Website Fixes"
                                description="Open a project first, then run quality checks or section improvements."
                                to="/projects"
                            />
                        </div>
                    </div>

                    <div className="dash-panel dash-guidance-panel">
                        <span className="project-kicker">Recommended Flow</span>
                        <h3>How to safely update your website</h3>

                        <div className="dash-step-list">
                            <div>
                                <strong>1. Edit content</strong>
                                <p>Open a project and update modules like programs, products, listings, or testimonials.</p>
                            </div>

                            <div>
                                <strong>2. Save & Publish</strong>
                                <p>Publishing saves your CRM content and starts the live website update.</p>
                            </div>

                            <div>
                                <strong>3. Check live site</strong>
                                <p>Use the deployment tracker to confirm the website is live and updated.</p>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}